import AtomNotice from '@/components/Atoms/AtomNotice/AtomNotice'
import { digitsMask } from '@/utils/forms/inputMasks'

import {
  type ConfigFieldContext,
  ConfigTextField,
} from '../ConfigFields/ConfigFields'
import cn from '../ConfigModal.module.scss'
import {
  type ColumnKey,
  DUPLICATE_COLUMN_MESSAGE,
} from '../lib/configColumnConflicts'
import {
  type PriceFieldContext,
  PriceTextField,
} from '../PricesStep/PriceFields'
import {
  columnRoles,
  type PriceColumnRole,
} from '../UrlExcelMapper/UrlExcelMapper.types'
import type { SegmentColumnsContextValue } from '../UrlExcelTable/SegmentColumnsContext'
import ConfigColumnFields from './ConfigColumnFields'
import ConfigSheetFields from './ConfigSheetFields'
import SegmentColumnFields from './SegmentColumnFields'

const FILE_FIELDS = [
  { value: 'startRowIndex', label: 'Начальная строка' },
  { value: 'endRowIndex', label: 'Конечная строка' },
  ...columnRoles,
] as const

const PRICE_COLUMN_PLACEHOLDER = 'A = 1, B = 2, C = 3'
const PRICE_COLUMN_TITLES = {
  purchaseColumnNumber: 'Колонка закупочной цены',
  rrcColumnNumber: 'Колонка РРЦ',
  currencyColumnNumber: 'Колонка валюты',
} as const

type ConfigFileFieldsProps = {
  context: ConfigFieldContext
  isFileLoaded: boolean
  priceContext?: PriceFieldContext
  priceRoles?: readonly { value: PriceColumnRole; label: string }[]
  segmentColumns: SegmentColumnsContextValue | null
  conflicts: Set<ColumnKey>
  showSheetFields: boolean
}

function ConfigFileFields({
  context,
  isFileLoaded,
  priceContext,
  priceRoles = [],
  segmentColumns,
  conflicts,
  showSheetFields,
}: ConfigFileFieldsProps) {
  if (isFileLoaded) {
    const fieldErrors = [
      ...FILE_FIELDS.flatMap((field) => {
        const message = context.errors[field.value]?.message
        return message ? [{ ...field, message }] : []
      }),
      ...(priceContext
        ? priceRoles.flatMap((field) => {
            const message = priceContext.errors[field.value]?.message
            return message ? [{ ...field, message }] : []
          })
        : []),
    ]
    if (fieldErrors.length === 0) return null

    return (
      <AtomNotice type="error" description="Выберите их в таблице файла">
        {fieldErrors.map((field) => (
          <div key={field.value}>
            {field.label}: {field.message}
          </div>
        ))}
      </AtomNotice>
    )
  }

  const renderPriceField = (role: PriceColumnRole) =>
    priceContext && (
      <PriceTextField
        key={role}
        context={priceContext}
        name={role}
        title={PRICE_COLUMN_TITLES[role]}
        placeholder={PRICE_COLUMN_PLACEHOLDER}
        mask={digitsMask}
        error={conflicts.has(role) ? DUPLICATE_COLUMN_MESSAGE : undefined}
      />
    )
  const hasPriceRole = (role: PriceColumnRole) =>
    priceRoles.some((item) => item.value === role)

  return (
    <>
      <div className={cn.grid}>
        <ConfigTextField
          context={context}
          name="startRowIndex"
          title="Начальная строка"
          placeholder="Начало файла"
          mask={digitsMask}
        />
        <ConfigTextField
          context={context}
          name="endRowIndex"
          title="Конечная строка"
          placeholder="До конца файла"
          mask={digitsMask}
        />
        {showSheetFields && <ConfigSheetFields context={context} />}
      </div>
      <div className={cn.grid}>
        <ConfigColumnFields context={context} conflicts={conflicts} />
        {hasPriceRole('purchaseColumnNumber') &&
          renderPriceField('purchaseColumnNumber')}
        {hasPriceRole('rrcColumnNumber') && renderPriceField('rrcColumnNumber')}
        {priceContext && segmentColumns && (
          <SegmentColumnFields
            segmentColumns={segmentColumns}
            conflicts={conflicts}
            isOrganisationSelected={priceContext.isOrganisationSelected}
          />
        )}
        {hasPriceRole('currencyColumnNumber') &&
          renderPriceField('currencyColumnNumber')}
      </div>
    </>
  )
}

export default ConfigFileFields
